import Head from 'next/head';
import { AdsView } from '@/views/Ads/Ads.view';
import { withPreRenderSettings } from '@/utils/hocs';
import { AdView } from '@/views/Ads/Ad.view';
import { EditAdView } from '@/views/Ads/EditAd.view';
import { getSeoFullData } from '@/api/helpers/seo.helpers';
import { ServerDataProvider } from '@/contexts';
import {
  getNewAdDefaultValues,
  getSearchResultsServerData,
  getServerAdDetails,
} from '@/api/helpers/ad.helpers';
import { AdDetails, NewAdDefaultValues } from '@/api/types/ads.types';
import { NewAdView } from '@/views/Ads/NewAd.view';
import NoSsr from '@/components/NoSsr/NoSsr';
import { usePageType } from '@/hooks/usePageType';
import { PageType } from '@/hooks/usePageType/usePageType.types';
import {
  CATEGORIES_SLUGS_COUNT,
  QUERY,
  ROOT_CATEGORY_SLUG,
} from '@/constants/common';
import { getCurrentCategory } from '@/api/helpers/categories.helpers';
import { NEW_AD_PAGE_SLUG } from '@/hooks/usePageType/usePageType.constants';
import { SeoFullData } from '@/api/types/seo.types';
import { CategoryBaseInfo } from '@/api/types/categories.types';
import { getRegionsCached } from '@/api/helpers/regions.helpers';
import { Ad } from '@/api/types/common.types';

interface Props {
  adDetails: AdDetails | null;
  refferer: string;
  newAdDefaultValues: NewAdDefaultValues | null;
  currentCategory: CategoryBaseInfo | null;
  seoFullData: SeoFullData | null;
  initialVipAds: Ad[] | null;
  initialLatestAds: Ad[] | null;
}

function AdsPage({
  adDetails,
  newAdDefaultValues,
  currentCategory,
  seoFullData,
  initialVipAds,
  initialLatestAds,
}: Props) {
  const pageType = usePageType();
  const { h1, title } = seoFullData || {};

  switch (pageType) {
    case PageType.NewAd:
      return (
        <NoSsr>
          <NewAdView {...newAdDefaultValues} />
        </NoSsr>
      );
    case PageType.EditAd:
      return (
        <ServerDataProvider adDetails={adDetails}>
          <EditAdView />
        </ServerDataProvider>
      );
    case PageType.Ad:
      return (
        <ServerDataProvider adDetails={adDetails}>
          <Head>
            <meta
              name="robots"
              content="max-image-preview:large"
              key="max-image-preview"
            />
          </Head>

          <AdView />
        </ServerDataProvider>
      );
    default:
      return (
        <ServerDataProvider
          currentCategory={currentCategory}
          h1={h1 || title}
          vipAds={initialVipAds}
          latestAds={initialLatestAds}
        >
          <AdsView />
        </ServerDataProvider>
      );
  }
}

AdsPage.getInitialProps = withPreRenderSettings(
  async (context, client, deviceType) => {
    const isServer = !!context.req;
    const pathname = context.asPath?.split('?')[0].slice(1) || '';

    const isSearchResultsPage =
      pathname.split('/').length <= CATEGORIES_SLUGS_COUNT &&
      !pathname.endsWith(NEW_AD_PAGE_SLUG);
    const isNewAdPage = pathname.endsWith(`/${NEW_AD_PAGE_SLUG}`);
    const isRootCategory = pathname === ROOT_CATEGORY_SLUG;

    const [currentCategory, regionsData] = await Promise.all([
      getCurrentCategory(client, context),
      isSearchResultsPage || isNewAdPage
        ? getRegionsCached(client, context)
        : null,
    ]);

    if (
      isSearchResultsPage &&
      !currentCategory?.id &&
      !isRootCategory &&
      context.res
    ) {
      context.res.statusCode = 404;
      return { seoFullData: null, adDetails: null, newAdDefaultValues: null };
    }

    const isUserAdsPage = !!context.query[QUERY.user_id];
    const isVipPage = pathname.endsWith('/vip');

    const [seoFullData, { adDetails }, newAdDefaultValues, searchAds] =
      await Promise.all([
        getSeoFullData(client, context, currentCategory?.id, regionsData),
        getServerAdDetails(client, context, deviceType),
        getNewAdDefaultValues(client, context, regionsData),
        isSearchResultsPage && !isUserAdsPage && isServer
          ? getSearchResultsServerData(
              client,
              context,
              deviceType,
              currentCategory,
              regionsData,
              isVipPage,
              isRootCategory
            )
          : Promise.resolve({ initialVipAds: null, initialLatestAds: null }),
      ]);

    return {
      seoFullData: seoFullData ?? null,
      adDetails: adDetails ?? null,
      newAdDefaultValues: newAdDefaultValues ?? null,
      currentCategory: currentCategory ?? null,
      initialVipAds: searchAds.initialVipAds,
      initialLatestAds: searchAds.initialLatestAds,
    };
  }
);

export default AdsPage;
